// Text input. Labeled, accessible, big enough to tap.

import type { InputHTMLAttributes } from "react";
import { forwardRef } from "react";

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
};

const wrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
};

const labelStyle: React.CSSProperties = {
  fontSize: "var(--font-size-14)",
  color: "var(--text-secondary)",
  fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  minHeight: 48,
  padding: "0 var(--space-3)",
  fontSize: "var(--font-size-16)",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-default)",
  background: "var(--surface-base)",
  color: "var(--text-primary)",
  outline: "none",
  width: "100%",
};

const errorStyle: React.CSSProperties = {
  fontSize: "var(--font-size-14)",
  color: "var(--color-danger)",
  fontWeight: 500,
};

export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  { label, error, id, style, ...rest },
  ref
) {
  const inputId = id ?? `input-${label?.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div style={wrapStyle}>
      {label && <label style={labelStyle} htmlFor={inputId}>{label}</label>}
      <input
        ref={ref}
        id={inputId}
        style={{
          ...inputStyle,
          borderColor: error ? "var(--color-danger)" : "var(--border-default)",
          ...style,
        }}
        {...rest}
      />
      {error && <span style={errorStyle} role="alert">{error}</span>}
    </div>
  );
});
